import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Avatar,
  Chip,
  Stack,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { 
  AutoAwesome, 
  GroupsOutlined, 
  CheckCircleOutlined,
  ArrowForwardOutlined,
  MenuBookOutlined,
  PsychologyOutlined,
  SpeedOutlined,
  Security
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SignInModal from './Auth/SignInModal';
import SignUpModal from './Auth/SignUpModal';
import SEO, { organizationSchema, softwareApplicationSchema, faqSchema } from '../utils/SEO';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);

  // Structured data combining Organization, SoftwareApplication, and FAQ
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      softwareApplicationSchema,
      faqSchema([
        {
          question: "What is Level Up Teaching?",
          answer: "Level Up Teaching is an AI-powered lesson planning platform that helps teachers create standards-aligned content, differentiated instruction materials, and professional assessments in minutes."
        },
        {
          question: "How does AI lesson planning work?",
          answer: "Our AI analyzes your requirements (grade level, topic, standards) and generates complete lesson plans with stories, activities, and assessments tailored to your students' needs."
        },
        {
          question: "Is content aligned with educational standards?",
          answer: "Yes, all generated content automatically aligns with curriculum standards including TEKS and Common Core, ensuring educational compliance."
        },
        {
          question: "Can I try Level Up Teaching for free?",
          answer: "Yes, we offer a first month free trial with no credit card required. You can cancel anytime."
        },
        {
          question: "What data do you access from Google Classroom?",
          answer: "We only access your name, email, classroom rosters, student lists, and course information to help you import students and organize reading groups. We never sell or share your data."
        }
      ])
    ]
  };

  // Handle custom events for modal switching
  useEffect(() => {
    const handleOpenSignInModal = () => {
      setSignUpModalOpen(false);
      setSignInModalOpen(true);
    };

    const handleOpenSignUpModal = () => {
      setSignInModalOpen(false);
      setSignUpModalOpen(true);
    };

    window.addEventListener('openSignInModal', handleOpenSignInModal);
    window.addEventListener('openSignUpModal', handleOpenSignUpModal);

    return () => {
      window.removeEventListener('openSignInModal', handleOpenSignInModal);
      window.removeEventListener('openSignUpModal', handleOpenSignUpModal);
    };
  }, []);

  const features = [
    {
      icon: <PsychologyOutlined sx={{ fontSize: 40 }} />,
      title: "AI-Powered Lesson Creation",
      description: "Generate complete lesson plans with stories, activities, and assessments in minutes using advanced AI technology.",
      color: theme.palette.primary.main
    },
    {
      icon: <MenuBookOutlined sx={{ fontSize: 40 }} />,
      title: "Standards-Aligned Content",
      description: "Every lesson automatically aligns with curriculum standards (TEKS, Common Core) ensuring educational compliance.",
      color: theme.palette.secondary.main
    },
    {
      icon: <GroupsOutlined sx={{ fontSize: 40 }} />,
      title: "Differentiated Instruction",
      description: "Create materials for multiple reading levels and learning styles with customizable Lexile targeting.",
      color: theme.palette.success.main
    },
    {
      icon: <SpeedOutlined sx={{ fontSize: 40 }} />,
      title: "Instant Export & Print",
      description: "Download professional PDF and Word documents ready for immediate classroom use.",
      color: theme.palette.warning.main
    }
  ];



  return (
    <>
      <SEO
        title="Level Up Teaching - AI-Powered Lesson Planning for Teachers"
        description="Create perfect lesson plans in minutes with AI. Generate standards-aligned content, differentiated instruction materials, and professional assessments for your classroom. Free trial available."
        keywords="AI lesson planning, teacher tools, educational technology, lesson plan generator, standards-aligned content, TEKS, Common Core, differentiated instruction, classroom resources, teaching assistant, AI for teachers, educational AI"
        canonicalUrl="https://levelupteaching.ai/"
        structuredData={structuredData}
      />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        {/* Header */}
        <Box 
        sx={{ 
          py: 2, 
          px: 3, 
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backdropFilter: 'blur(10px)'
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                component="span"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: 'common.white',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box component="img" src={`${process.env.PUBLIC_URL}/icon.png`} alt="Level Up Teaching" sx={{ width: '90%', height: '90%' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary">
                Level Up Teaching
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button 
                variant="outlined" 
                onClick={() => setSignInModalOpen(true)}
                sx={{ borderRadius: 3 }}
              >
                Sign In
              </Button>
              <Button 
                variant="contained" color="primary"
                onClick={() => setSignUpModalOpen(true)}
                sx={{ borderRadius: 3, fontWeight: 'bold' }}
                endIcon={<ArrowForwardOutlined />}
              >
                Start Free Trial
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 12 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              <Chip 
                label="AI-Powered Education" 
                icon={<AutoAwesome />}
                sx={{ 
                  alignSelf: 'flex-start',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 'bold'
                }}
              />
              <Typography 
                variant="h1" 
                fontWeight="bold" 
                sx={{ 
                  fontSize: { xs: '2.25rem', md: '3rem' },
                  lineHeight: 1.2,
                  color: 'text.primary'
                }}
              >
                Create Perfect Lesson Plans in Minutes
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ fontSize: '1.25rem', lineHeight: 1.6 }}
              >
                AI-powered lesson planning for teachers. Generate standards-aligned content 
                and professional assessments in seconds.
              </Typography>
              <Stack direction="row" spacing={3} sx={{ pt: 2 }}>
                <Button 
                  variant="contained" color="primary"
                  size="large"
                  onClick={() => setSignUpModalOpen(true)}
                  sx={{ 
                    borderRadius: 3, 
                    py: 1.5, 
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    textTransform: 'none'
                  }}
                  endIcon={<ArrowForwardOutlined />}
                >
                  Start Free Trial
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                First month free — cancel anytime.
              </Typography>
              <Stack direction="row" spacing={4} sx={{ pt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleOutlined color="success" />
                  <Typography variant="body2" color="text.secondary">
                    No credit card required
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4, 
                borderRadius: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold">
                  ✨ Generate a lesson plan instantly
                </Typography>
                <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Topic: Friendship • Grade 3 • Lexile 500L
                  </Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    "Create engaging activities about friendship with vocabulary preview, 
                    reading comprehension, and discussion questions..."
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                    <CheckCircleOutlined />
                  </Avatar>
                  <Typography variant="body1" fontWeight="medium">
                    Complete lesson generated in 15 seconds
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>



      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Stack spacing={8}>
          <Stack alignItems="center" spacing={3}>
            <Typography variant="h3" fontWeight="bold" textAlign="center">
              Everything You Need for Modern Teaching
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center" maxWidth="md">
              Powerful AI tools designed specifically for educators to create engaging, 
              standards-aligned content that saves time and improves student outcomes.
            </Typography>
          </Stack>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={3} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    p: 3,
                    border: `1px solid ${alpha(feature.color, 0.2)}`,
                    borderRadius: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(feature.color, 0.15)}`
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Stack spacing={3}>
                      <Avatar 
                        sx={{ 
                          bgcolor: alpha(feature.color, 0.1),
                          color: feature.color,
                          width: 70,
                          height: 70
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Stack spacing={2}>
                        <Typography variant="h5" fontWeight="bold">
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                          {feature.description}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Data Usage & Privacy Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 6, 
            borderRadius: 4,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}
        >
          <Stack spacing={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Your Privacy & Data Usage
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Transparency in how we access and protect your information
              </Typography>
            </Box>

            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  >
                    <Security sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    What We Access
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    When you connect Google Classroom, we access:
                  </Typography>
                  <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Your name and email address
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Google Classroom rosters and student lists
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Course information (names and sections)
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main
                    }}
                  >
                    <CheckCircleOutlined sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    Why We Need It
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    This data enables core features:
                  </Typography>
                  <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Import student rosters automatically
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Organize students into reading groups
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Create differentiated lesson plans
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  >
                    <GroupsOutlined sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    How We Use It
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your data is protected:
                  </Typography>
                  <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Used solely for educational purposes
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Never sold or shared with third parties
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Encrypted and stored securely
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', pt: 3 }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                You can revoke our access to your Google account at any time through your Google Account settings. 
                For complete details, please review our comprehensive privacy policy.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                <Button 
                  variant="outlined"
                  onClick={() => navigate('/privacy-policy')}
                  sx={{ borderRadius: 2 }}
                >
                  Read Privacy Policy
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => navigate('/terms-of-service')}
                  sx={{ borderRadius: 2 }}
                >
                  View Terms of Service
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 8, 
            borderRadius: 4,
            background: 'linear-gradient(90deg, #2563EB 0%, #8B5CF6 50%, #F97316 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Stack spacing={4} alignItems="center">
            <Typography variant="h3" fontWeight="bold">
              Ready to Transform Your Teaching?
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 'md' }}>
              Join thousands of teachers creating better lessons with AI — start your first month free today!
            </Typography>
            <Button 
              variant="contained" color="primary"
              size="large"
              onClick={() => setSignUpModalOpen(true)}
              sx={{ 
                borderRadius: 3, 
                py: 1.5, 
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                textTransform: 'none',
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.9)
                }
              }}
              endIcon={<ArrowForwardOutlined />}
            >
              Start Free Trial
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* Footer */}
      <Box 
        sx={{ 
          py: 6, 
          px: 3,
          bgcolor: alpha(theme.palette.grey[900], 0.05),
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Container maxWidth="lg">
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            justifyContent="space-between" 
            alignItems="center"
            spacing={3}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box component="span" sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'common.white', border: '2px solid', borderColor: 'primary.main', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={`${process.env.PUBLIC_URL}/icon.png`} alt="Level Up Teaching" sx={{ width: '88%', height: '88%' }} />
              </Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Level Up Teaching
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              © 2024 Level Up Teaching. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={4}>
              <Button 
                color="inherit" 
                sx={{ textTransform: 'none' }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy
              </Button>
              <Button 
                color="inherit" 
                sx={{ textTransform: 'none' }}
                onClick={() => navigate('/terms-of-service')}
              >
                Terms
              </Button>
              <Button 
                color="inherit" 
                sx={{ textTransform: 'none' }}
                onClick={() => navigate('/contact')}
              >
                Support
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

        {/* Auth Modals */}
        <SignInModal 
          open={signInModalOpen}
          onClose={() => setSignInModalOpen(false)}
        />
        <SignUpModal 
          open={signUpModalOpen}
          onClose={() => setSignUpModalOpen(false)}
        />
      </Box>
    </>
  );
};

export default Home; 